import React from 'react';
import { Box, Flex, Heading, Stack, Text, Tooltip } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { TrendUp } from 'phosphor-react';

import { GlassCard } from '../../../../../components/glass';
import type { IContaCilMesStatus } from '../../../../../hooks/useContaCilDashboard';

const MESES_ABBR = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

const formatNumber = (n: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(n);

const Linha = ({ label, valor, cor }: { label: string; valor: string; cor: string }) => (
  <Flex justify="space-between" align="baseline">
    <Text fontSize="xs" color="#64748B" fontWeight="500">
      {label}
    </Text>
    <Text fontSize="xs" fontWeight="700" color={cor} sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {valor}
    </Text>
  </Flex>
);

interface YearDetailPanelProps {
  meses: IContaCilMesStatus[];
  ano: number;
}

// Detalhe do ANO — mostrado quando nenhum mês está selecionado. Tendência
// mês-a-mês (conciliado vs pendente) + destaques, a partir do meses_status que
// o /dashboard/ já devolve (sem endpoint novo).
export const YearDetailPanel = ({ meses, ano }: YearDetailPanelProps) => {
  const totalLanc = meses.reduce((s, m) => s + m.total, 0);
  const totalConc = meses.reduce((s, m) => s + m.conciliados, 0);
  const percentAno = totalLanc > 0 ? (totalConc / totalLanc) * 100 : 0;
  const maxTotal = Math.max(1, ...meses.map(m => m.total));

  const comMov = meses.filter(m => m.total > 0);
  const melhorMes =
    comMov.length > 0
      ? comMov.reduce((best, m) =>
          m.conciliados / Math.max(1, m.total) > best.conciliados / Math.max(1, best.total)
            ? m
            : best,
        )
      : null;
  const piorPendente =
    comMov.length > 0 ? comMov.reduce((w, m) => (m.pendentes > w.pendentes ? m : w)) : null;
  const mesPico = comMov.length > 0 ? comMov.reduce((p, m) => (m.total > p.total ? m : p)) : null;
  const mesesAtivos = comMov.length;
  const mediaAtivo = mesesAtivos > 0 ? Math.round(totalLanc / mesesAtivos) : 0;
  const picoEmpresas = Math.max(0, ...meses.map(m => m.empresas));

  if (totalLanc === 0) {
    return (
      <GlassCard intensity="medium" p={6} h="full" minH="280px">
        <Flex
          direction="column"
          align="center"
          justify="center"
          h="full"
          textAlign="center"
          gap={3}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            w="48px"
            h="48px"
            borderRadius="full"
            bg="rgba(99, 102, 241, 0.10)"
            color="#6366F1"
          >
            <TrendUp size={24} weight="duotone" />
          </Box>
          <Box>
            <Text fontSize="sm" fontWeight="600" color="#0F172A" mb={1}>
              Sem movimento em {ano}
            </Text>
            <Text fontSize="xs" color="#64748B" lineHeight="1.5" maxW="240px">
              Nenhum lançamento importado neste ano ainda. Toque num mês do calendário para ver o
              detalhe.
            </Text>
          </Box>
        </Flex>
      </GlassCard>
    );
  }

  return (
    <GlassCard intensity="medium" p={6} h="full" minH="280px">
      <Stack spacing={5}>
        <Box>
          <Text
            fontSize="10px"
            fontWeight="600"
            color="#94A3B8"
            textTransform="uppercase"
            letterSpacing="0.08em"
            mb={1}
          >
            Detalhe do ano
          </Text>
          <Heading as="h3" fontSize="xl" fontWeight="700" color="#0F172A" letterSpacing="-0.02em">
            Panorama {ano}
          </Heading>
          <Text fontSize="xs" color="#64748B" mt={1}>
            {formatNumber(totalLanc)} lançamentos · {percentAno.toFixed(0)}% conciliado no ano
          </Text>
        </Box>

        {/* Tendência mês-a-mês: barra empilhada conciliado (verde) vs resto */}
        <Box>
          <Text
            fontSize="10px"
            fontWeight="600"
            color="#94A3B8"
            textTransform="uppercase"
            letterSpacing="0.06em"
            mb={3}
          >
            Evolução mensal
          </Text>
          <Flex align="flex-end" justify="space-between" gap={1} h="96px">
            {meses.map((m, i) => {
              const h = (m.total / maxTotal) * 100;
              const pctConc = m.total > 0 ? (m.conciliados / m.total) * 100 : 0;
              return (
                <Tooltip
                  key={m.mes}
                  label={`${MESES_ABBR[i]}: ${formatNumber(m.total)} lanç. · ${pctConc.toFixed(0)}% conc.`}
                  fontSize="xs"
                  hasArrow
                >
                  <Flex
                    direction="column"
                    align="center"
                    gap={1}
                    flex={1}
                    h="full"
                    justify="flex-end"
                  >
                    <Box
                      position="relative"
                      w="full"
                      maxW="16px"
                      h={`${Math.max(h, m.total > 0 ? 6 : 0)}%`}
                      minH={m.total > 0 ? '4px' : '0'}
                    >
                      <motion.div
                        initial={{ scaleY: 0 }}
                        animate={{ scaleY: 1 }}
                        transition={{ duration: 0.4, delay: 0.03 * i, ease: [0.4, 0, 0.2, 1] }}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          transformOrigin: 'bottom',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          background: 'rgba(245, 158, 11, 0.35)',
                        }}
                      >
                        <Box
                          position="absolute"
                          bottom={0}
                          left={0}
                          right={0}
                          h={`${pctConc}%`}
                          bg="#10B981"
                        />
                      </motion.div>
                    </Box>
                    <Text fontSize="9px" color="#94A3B8" fontWeight="600">
                      {MESES_ABBR[i]}
                    </Text>
                  </Flex>
                </Tooltip>
              );
            })}
          </Flex>
          <Flex gap={4} mt={3} justify="center">
            <Flex align="center" gap={1.5}>
              <Box w="8px" h="8px" borderRadius="sm" bg="#10B981" />
              <Text fontSize="10px" color="#64748B">
                Conciliado
              </Text>
            </Flex>
            <Flex align="center" gap={1.5}>
              <Box w="8px" h="8px" borderRadius="sm" bg="rgba(245, 158, 11, 0.5)" />
              <Text fontSize="10px" color="#64748B">
                Pendente/outros
              </Text>
            </Flex>
          </Flex>
        </Box>

        {/* Destaques do ano */}
        <Box borderTopWidth="1px" borderColor="rgba(15, 23, 42, 0.06)" pt={4}>
          <Text
            fontSize="10px"
            fontWeight="600"
            color="#94A3B8"
            textTransform="uppercase"
            letterSpacing="0.06em"
            mb={2.5}
          >
            Destaques
          </Text>
          <Stack spacing={2.5}>
            <Linha label="Meses com movimento" valor={`${mesesAtivos} de 12`} cor="#0F172A" />
            {mesPico && (
              <Linha
                label="Mês de maior volume"
                valor={`${MESES_ABBR[mesPico.mes - 1]} · ${formatNumber(mesPico.total)}`}
                cor="#6366F1"
              />
            )}
            <Linha
              label="Média por mês ativo"
              valor={`${formatNumber(mediaAtivo)} lanç.`}
              cor="#0F172A"
            />
            <Linha label="Pico de empresas/mês" valor={formatNumber(picoEmpresas)} cor="#7C3AED" />
            {melhorMes && (
              <Linha
                label="Mês mais conciliado"
                valor={`${MESES_ABBR[melhorMes.mes - 1]} · ${(
                  (melhorMes.conciliados / Math.max(1, melhorMes.total)) *
                  100
                ).toFixed(0)}%`}
                cor="#10B981"
              />
            )}
            {piorPendente && piorPendente.pendentes > 0 && (
              <Linha
                label="Mais pendências"
                valor={`${MESES_ABBR[piorPendente.mes - 1]} · ${formatNumber(
                  piorPendente.pendentes,
                )} pend.`}
                cor="#F59E0B"
              />
            )}
          </Stack>
        </Box>
      </Stack>
    </GlassCard>
  );
};
